/**
 * 시드 고정 가능한 결정적 난수 (지시서 §2, §7).
 * 같은 시드 + 같은 호출 순서 = 같은 수열. 리플레이/헤드리스 테스트의 전제.
 * sim-core 전역에서 Math.random 직접 호출 금지 — 반드시 이 Rng를 경유한다.
 *
 * 알고리즘: mulberry32 (빠르고 통계 품질 충분, 32비트 상태로 완전 재현 가능).
 */
export class Rng {
  private s: number;

  constructor(seed: number) {
    // 32비트 부호없음으로 정규화. 0 시드도 허용(내부에서 상수 가산).
    this.s = seed >>> 0;
  }

  /** [0, 1) 실수. */
  next(): number {
    this.s = (this.s + 0x6d2b79f5) | 0;
    let t = this.s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  /** [0, n) 정수. n<=0이면 0. */
  int(n: number): number {
    if (n <= 0) return 0;
    return Math.floor(this.next() * n);
  }

  /** [min, max) 실수. */
  range(min: number, max: number): number {
    return min + this.next() * (max - min);
  }

  /** 확률 p로 true. */
  chance(p: number): boolean {
    return this.next() < p;
  }

  /** 배열에서 균등 추출. 빈 배열이면 예외. */
  pick<T>(arr: readonly T[]): T {
    if (arr.length === 0) throw new Error('Rng.pick: 빈 배열');
    return arr[this.int(arr.length)] as T;
  }

  /**
   * 독립된 하위 스트림 생성. 시스템별로 rng를 분기해도 서로 결정성을 해치지 않게 한다.
   * (예: 지형 시드용, 개체 판단용을 분리)
   */
  fork(salt: number): Rng {
    return new Rng((this.s ^ Math.imul(salt >>> 0, 0x9e3779b1)) >>> 0);
  }

  /** 현재 내부 상태 (스냅샷/재개용). */
  get state(): number {
    return this.s >>> 0;
  }

  /** 상태 직접 복원. */
  set state(v: number) {
    this.s = v >>> 0;
  }
}

export function makeRng(seed: number): Rng {
  return new Rng(seed);
}
